// "Getting started": the way from an application to a shop that sells, as
// five steps at the top of every page of the portal — the application
// approved, the company's details complete, the payout contract signed, a
// first product, a product sent for review. Each is done, the current one
// (the first not done) or still to come; the current one says what it waits
// for and opens the place to do it. Numbered circles on a line from 768px, a
// short list on a phone. It folds into one line ("Getting started · step 3 of
// 5: Contract"), remembered per organization, and is gone once all five are
// done. The company checklist under it stays as it is: it is the detail of
// step 2 (and of the contract).
import React, { useEffect, useState } from "react"
import ProgressBar from "aldoo-ra/ProgressBar"
import Button from "aldoo-ra/Button"
import { CheckIcon } from "@heroicons/react/24/solid"
import { ChevronDownIcon, RocketLaunchIcon, ArrowRightIcon } from "@heroicons/react/24/outline"
import usePortalTexts from "./texts/usePortalTexts"
import { PORTAL } from "./texts/bundles"
import { eligibilityOf } from "./EligibilityChecklist"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

// the help center's article on the way to selling (aldoo-com-api scripts/help-center/vendor/articles/getting-started.html): its section on each step
const HELP = { application: "vendor.getting-started#apply", company: "vendor.getting-started#company", contract: "vendor.getting-started#contract", products: "vendor.getting-started#first-product", review: "vendor.getting-started#review" }

/**
 * The five steps of a company: [{ key, label, done, hint, action }] —
 * `action` { label, run } is what the step's button does (none: nothing for
 * the company to do, Aldoo's turn). `go` opens the places:
 * go.fix(item) a checklist item, go.contract(), go.newProduct(), go.products(),
 * go.registration().
 */
export function onboardingSteps({ org, physical = false, payouts = null, onboarding = null, productCount = 0, items = null }, go) {
  if (!org) return []
  const approved = onboarding ? !!onboarding.applicationApproved : !["pending", "rejected"].includes(org.verification)
  const rejected = org.verification === "rejected"
  // step 2 is the checklist without its contract item (step 3 is the contract)
  const details = eligibilityOf(org, { physical, payouts, items }).filter((item) => !item.optional && item.key !== "payoutContract")
  const missing = details.filter((item) => !item.done)
  // before approval only Company is open (Delivery and Settings come with it): the first item there
  const firstMissing = (approved ? missing : missing.filter((item) => item.section === "company"))[0] || null
  const payoutItem = details.find((item) => item.key === "payoutDetails")
  const products = Number(onboarding?.productCount ?? productCount) || 0
  const sent = Number(onboarding?.productsSubmitted) || 0

  const application = {
    key: "application",
    label: "Application",
    done: approved,
    hint: rejected ? "Your application was not approved" : "We are reviewing your application",
    sub: rejected ? null : "You will get an email once it is decided. Meanwhile, fill in your company details.",
    // waiting for Aldoo is the time to fill in the company's details
    action: rejected ? { label: "See why", run: go.registration } : firstMissing ? { label: "Complete the details", run: () => go.fix(firstMissing) } : null,
  }

  const company = {
    key: "company",
    label: "Company details",
    done: !missing.length,
    hint: "Fill in your company, contact, payout and delivery details",
    progress: [details.length - missing.length, details.length],
    next: (firstMissing || missing[0])?.label || null,
    action: firstMissing ? { label: "Complete the details", run: () => go.fix(firstMissing) } : null,
  }

  const contract = {
    key: "contract",
    label: "Contract",
    done: !!payouts?.contractSigned,
    ...(payouts?.contractPending
      ? { hint: "Your payout contract is ready: review and sign it", action: { label: "Review and sign", run: go.contract } }
      : onboarding?.contractWaiting && payoutItem && !payoutItem.done
        ? { hint: "Complete your payout details to receive the contract", action: { label: "Complete the payout details", run: () => go.fix(payoutItem) } }
        : approved
          ? { hint: "Aldoo will send your contract", sub: "You will get an email when it is ready to sign.", action: null }
          : { hint: "It is sent once your application is approved", action: null }),
  }

  const product = {
    key: "products",
    label: "Products",
    done: products > 0,
    ...(approved ? { hint: "Add your first product", action: { label: "New product", run: go.newProduct } } : { hint: "Available once your application is approved", action: null }),
  }

  const review = {
    key: "review",
    label: "Send a product for review",
    done: sent > 0,
    ...(!approved
      ? { hint: "Available once your application is approved", action: null }
      : products > 0
        ? {
            hint: "Send a product for review: it is published once it is approved",
            // straight to the product waiting to be sent, on its Product Review tab; else the list
            action: onboarding?.productToSend ? { label: "Open the product", run: () => go.reviewProduct(onboarding.productToSend) } : { label: "Open products", run: go.products },
          }
        : { hint: "Add a product first, then send it for review", action: null }),
  }

  return [application, company, contract, product, review]
}

const storageKey = (orgId) => `vendor-onboarding-collapsed:${orgId}`
const readCollapsed = (orgId) => {
  try {
    return window.localStorage.getItem(storageKey(orgId)) === "1"
  } catch {
    return false
  }
}
const writeCollapsed = (orgId, collapsed) => {
  try {
    if (collapsed) window.localStorage.setItem(storageKey(orgId), "1")
    else window.localStorage.removeItem(storageKey(orgId))
  } catch {
    // no storage (a private window): it opens expanded next time
  }
}

/** The step's circle: a tick when done, its number otherwise; the current one lit. */
const Circle = ({ state, n, className = "" }) => (
  <span
    className={`relative z-10 w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${
      state === "done"
        ? "bg-admin_success text-white"
        : state === "current"
          ? "bg-admin_primary text-white ring-4 ring-admin_primary/15"
          : "bg-admin_paper border-2 border-admin_bordered text-admin_text_secondary"
    } ${className}`}
    aria-hidden
  >
    {state === "done" ? <CheckIcon className="w-4 h-4" /> : n}
  </span>
)

/** What the current step waits for, and the way to it. */
const CurrentHint = ({ step, t, className = "" }) => (
  <div className={`flex flex-col sm:flex-row sm:items-center gap-3 ${className}`} data-testid="onboarding-hint">
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-admin_text inline-flex items-center gap-1.5">{t(step.hint)}<HelpHint helpKey={HELP[step.key]} /></div>
      {step.progress && (
        <div className="text-xs text-admin_text_secondary mt-0.5">
          {t("%s of %s done", step.progress[0], step.progress[1])}
          {step.next && <> · {t("Next: %s", t(step.next))}</>}
        </div>
      )}
      {step.sub && <div className="text-xs text-admin_text_secondary mt-0.5">{t(step.sub)}</div>}
    </div>
    {step.action && (
      <Button
        variant="admin_primary"
        onClick={step.action.run}
        className="h-[36px] text-sm shrink-0 self-start sm:self-auto"
        data-testid={`onboarding-action-${step.key}`}
      >
        {t(step.action.label)}
        <ArrowRightIcon className="w-4 h-4" />
      </Button>
    )}
  </div>
)

export default function OnboardingGuide({ org, physical = false, payouts = null, onboarding = null, productCount = 0, items = null, go }) {
  const t = usePortalTexts(PORTAL)
  const orgId = org?._id
  const [collapsed, setCollapsed] = useState(false)
  // the browser's own memory of it, read once the organization is known (not during the server render)
  useEffect(() => {
    if (orgId) setCollapsed(readCollapsed(orgId))
  }, [orgId])

  const steps = onboardingSteps({ org, physical, payouts, onboarding, productCount, items }, go)
  const done = steps.filter((step) => step.done).length
  if (!steps.length || done === steps.length) return null
  const currentIndex = steps.findIndex((step) => !step.done)
  const current = steps[currentIndex]
  const stateOf = (step, i) => (step.done ? "done" : i === currentIndex ? "current" : "todo")

  const toggle = () => {
    writeCollapsed(orgId, !collapsed)
    setCollapsed(!collapsed)
  }

  return (
    <section 
      className="rounded-lg border border-admin_primary/25 bg-admin_paper text-sm shadow-sm" 
      data-testid="onboarding-guide" 
      data-step={current.key} 
      data-collapsed={collapsed || undefined}
    >
      <button 
        type="button" 
        onClick={toggle} 
        className="w-full flex items-center gap-3 px-4 py-3 text-left" 
        aria-expanded={!collapsed} 
        data-testid="onboarding-toggle"
      >
        <RocketLaunchIcon className="w-5 h-5 shrink-0 text-admin_primary" />
        <span className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-baseline sm:gap-1.5">
          <span className="font-semibold">{t`Getting started`}</span>
          {/* folded: where the company is — beside the title, under it on a phone */}
          {collapsed && (
            <span className="text-admin_text_secondary truncate" data-testid="onboarding-summary">
              <span className="hidden sm:inline">· </span>
              {t("Step %s of %s: %s", currentIndex + 1, steps.length, t(current.label))}
            </span>
          )}
        </span>
        <span className="flex items-center gap-3 shrink-0">
          <span className={`${collapsed ? "block w-16" : "hidden"} sm:block sm:w-32 md:w-40`}>
            <ProgressBar
              progress={done / steps.length}
              variant="compact"
              styleOverrides={{ progressContainer: "h-2 w-full rounded-full bg-admin_neutral_1", progressFill: "h-2 rounded-full bg-admin_primary transition-all duration-300" }}
            />
          </span>
          <span className="hidden sm:inline text-admin_text_secondary whitespace-nowrap" data-testid="onboarding-count">
            {t("%s of %s done", done, steps.length)}
          </span>
        </span>
        <ChevronDownIcon className={`w-4 h-4 shrink-0 transition-transform ${collapsed ? "" : "rotate-180"}`} />
      </button>

      {!collapsed && (
        <div className="px-4 pb-4" data-testid="onboarding-steps">
          {/* from 768px: numbered circles on a line, the labels under them */}
          <ol className="hidden md:grid grid-cols-5 m-0 p-0 list-none pt-1">
            {steps.map((step, i) => {
              const state = stateOf(step, i)
              return (
                <li key={step.key} className="relative flex flex-col items-center text-center px-2" data-testid={`onboarding-step-${step.key}`} data-state={state}>
                  {/* the line from the step before: green once that one is done */}
                  {i > 0 && <span className={`absolute top-4 right-1/2 w-full h-0.5 -translate-y-1/2 ${steps[i - 1].done ? "bg-admin_success" : "bg-admin_bordered"}`} aria-hidden />}
                  <Circle state={state} n={i + 1} />
                  <span className={`mt-2 leading-tight ${state === "current" ? "font-bold text-admin_primary" : state === "done" ? "font-medium text-admin_text" : "text-admin_text_secondary"}`}>{t(step.label)}</span>
                  <span className={`text-xs mt-0.5 ${state === "done" ? "text-admin_success" : "text-admin_text_secondary"}`}>
                    {state === "done" ? t`Done` : state === "current" ? t`Next step` : " "}
                  </span>
                </li>
              )
            })}
          </ol>
          <CurrentHint step={current} t={t} className="hidden md:flex mt-4 rounded-md bg-admin_primary/5 border border-admin_primary/15 px-4 py-3" />

          {/* on a phone: a short list, the current step's hint and button under it */}
          <ol className="md:hidden m-0 p-0 list-none flex flex-col">
            {steps.map((step, i) => {
              const state = stateOf(step, i)
              const last = i === steps.length - 1
              return (
                <li key={step.key} className="relative flex gap-3 pb-3 last:pb-0" data-testid={`onboarding-row-${step.key}`} data-state={state}>
                  {/* the line down to the next step */}
                  {!last && <span className={`absolute left-4 top-8 bottom-0 w-0.5 -translate-x-1/2 ${step.done ? "bg-admin_success" : "bg-admin_bordered"}`} aria-hidden />}
                  <Circle state={state} n={i + 1} />
                  <div className="flex-1 min-w-0 pt-1.5">
                    <div className="flex items-center gap-2">
                      <span className={state === "current" ? "font-bold text-admin_primary" : state === "done" ? "font-medium" : "text-admin_text_secondary"}>{t(step.label)}</span>
                      {state === "done" && <span className="text-xs text-admin_success">{t`Done`}</span>}
                    </div>
                    {state === "current" && <CurrentHint step={step} t={t} className="mt-2" />}
                  </div>
                </li>
              )
            })}
          </ol>
        </div>
      )}
    </section>
  )
}
